import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useFetch } from "../../hooks/useFetch";
import { fetchDocumentImages } from "../../services/verificationsService";
import { isHttpError, isValidationError } from "../../errors/isApiError";
import type { DocumentImage } from "../../types/verification";

export interface DocumentImagesDialogProps {
  open: boolean;
  onClose: () => void;
  verificationId: string;
  documentId: string;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

function ZoomableImage({ image }: { image: DocumentImage }) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(MIN_ZOOM);

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
  const resetZoom = () => setZoom(MIN_ZOOM);

  return (
    <Stack spacing={1} alignItems="center" sx={{ maxWidth: 320 }}>
      <Typography variant="caption" color="text.secondary">
        {image.side}
      </Typography>
      <Box
        sx={{
          width: "100%",
          maxHeight: 360,
          overflow: "auto",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Box
          component="img"
          src={image.url}
          alt={image.side}
          sx={{
            display: "block",
            width: `${zoom * 100}%`,
            transition: "width 0.15s ease",
          }}
        />
      </Box>
      <Stack direction="row" spacing={0.5}>
        <IconButton
          size="small"
          aria-label={t("Zoom out")}
          onClick={zoomOut}
          disabled={zoom <= MIN_ZOOM}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={t("Reset zoom")}
          onClick={resetZoom}
          disabled={zoom === MIN_ZOOM}
        >
          <RestartAltIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={t("Zoom in")}
          onClick={zoomIn}
          disabled={zoom >= MAX_ZOOM}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
}

function DocumentImagesDialog({
  open,
  onClose,
  verificationId,
  documentId,
}: DocumentImagesDialogProps) {
  const { t } = useTranslation();
  const { data, isLoading, error, fetchData } = useFetch(fetchDocumentImages);

  useEffect(() => {
    if (!open) return;
    fetchData(verificationId, documentId).catch(() => {
      // Failure is already captured in `error` state via useFetch; swallow
      // here so a network error doesn't surface as an unhandled rejection.
    });
  }, [open, verificationId, documentId, fetchData]);

  const errorMessage =
    isHttpError(error) || isValidationError(error)
      ? error.message
      : error
        ? t("Failed to load document images.")
        : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {t("Document Images")}
        <IconButton aria-label={t("close")} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoading && errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {!isLoading && !errorMessage && data && data.images.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t("No images available for this document.")}
          </Typography>
        )}

        {!isLoading && !errorMessage && data && data.images.length > 0 && (
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={2}
            justifyContent="center"
            sx={{ width: "100%" }}
          >
            {data.images.map((image) => (
              <ZoomableImage key={image.side} image={image} />
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DocumentImagesDialog;
